import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { CharacterId, GiantId } from '../../../domain/ids/ids';
import {
  EDDY_LUCKY_SHOT_TIME_LIMIT_SEC,
  getSecondChanceMinigameId,
  ROCK_PUZZLE_TIME_LIMIT_SEC,
  TUCHI_MELODY_TIME_LIMIT_SEC,
  UFO_DECIPHER_TIME_LIMIT_SEC,
  VALL_DROP_BOX_TIME_LIMIT_SEC,
} from '../../../domain';
import { containFitScale } from '../../../infrastructure/runtime';
import { useAppFit } from '../../hooks/useAppFit';
import {
  getSecondChanceMinigameTitle,
  SecondChanceMinigameRouter,
} from './SecondChanceMinigameRouter';
import './SecondChanceMinigameOverlay.css';

export type SecondChanceMinigameOverlayProps = {
  characterId: CharacterId;
  giantId: GiantId;
  onWin: () => void;
  onLose: () => void;
};

function initialSeconds(characterId: CharacterId): number | null {
  const id = getSecondChanceMinigameId(characterId);
  if (id === 'rock_rotate_puzzle') {
    return ROCK_PUZZLE_TIME_LIMIT_SEC;
  }
  if (id === 'eddy_lucky_shot') {
    return EDDY_LUCKY_SHOT_TIME_LIMIT_SEC;
  }
  if (id === 'tuchi_melody') {
    return TUCHI_MELODY_TIME_LIMIT_SEC;
  }
  if (id === 'vall_drop_box') {
    return VALL_DROP_BOX_TIME_LIMIT_SEC;
  }
  if (id === 'ufo_decipher_words') {
    return UFO_DECIPHER_TIME_LIMIT_SEC;
  }
  return null;
}

export function SecondChanceMinigameOverlay({
  characterId,
  giantId,
  onWin,
  onLose,
}: SecondChanceMinigameOverlayProps) {
  const fit = useAppFit();
  const contain = containFitScale(fit.scale);
  const minigameId = getSecondChanceMinigameId(characterId);
  const isClaudiSkin = minigameId === 'claudi_find_pairs';
  const isRockSkin = minigameId === 'rock_rotate_puzzle';
  const isEddySkin = minigameId === 'eddy_lucky_shot';
  const isTuchiSkin = minigameId === 'tuchi_melody';
  const isVallSkin = minigameId === 'vall_drop_box';
  const isUfoSkin = minigameId === 'ufo_decipher_words';
  const [secondsLeft, setSecondsLeft] = useState<number | null>(() =>
    initialSeconds(characterId),
  );
  const settledRef = useRef(false);

  useEffect(() => {
    settledRef.current = false;
    setSecondsLeft(initialSeconds(characterId));
  }, [characterId]);

  const handleWin = useCallback(() => {
    if (settledRef.current) {
      return;
    }
    settledRef.current = true;
    onWin();
  }, [onWin]);

  const handleLose = useCallback(() => {
    if (settledRef.current) {
      return;
    }
    settledRef.current = true;
    onLose();
  }, [onLose]);

  const fitStyle = {
    '--fit-contain-inv-x': String(contain.invX),
    '--fit-contain-inv-y': String(contain.invY),
    '--fit-contain-uniform': String(contain.uniform),
  } as CSSProperties;

  const game = (
    <SecondChanceMinigameRouter
      characterId={characterId}
      giantId={giantId}
      onWin={handleWin}
      onLose={handleLose}
      onTimerChange={setSecondsLeft}
    />
  );

  if (isClaudiSkin || isRockSkin || isEddySkin || isTuchiSkin || isVallSkin || isUfoSkin) {
    const skinClass = isClaudiSkin
      ? 'second-chance-minigame-overlay--claudi'
      : isRockSkin
        ? 'second-chance-minigame-overlay--rock'
        : isEddySkin
          ? 'second-chance-minigame-overlay--eddy'
          : isTuchiSkin
            ? 'second-chance-minigame-overlay--tuchi'
            : isVallSkin
              ? 'second-chance-minigame-overlay--vall'
              : 'second-chance-minigame-overlay--ufo';
    const stageClass = isClaudiSkin
      ? 'second-chance-minigame-claudi-stage'
      : isRockSkin
        ? 'second-chance-minigame-rock-stage'
        : isEddySkin
          ? 'second-chance-minigame-eddy-stage'
          : isTuchiSkin
            ? 'second-chance-minigame-tuchi-stage'
            : isVallSkin
              ? 'second-chance-minigame-vall-stage'
              : 'second-chance-minigame-ufo-stage';
    return (
      <div className={`second-chance-minigame-overlay ${skinClass}`} style={fitStyle}>
        <div className={stageClass}>{game}</div>
      </div>
    );
  }

  return (
    <div className="second-chance-minigame-overlay" style={fitStyle}>
      <div className="second-chance-minigame-panel">
        <h2 className="second-chance-minigame-title">
          {getSecondChanceMinigameTitle(characterId)}
        </h2>
        <div className="second-chance-minigame-hud">
          <span />
          {secondsLeft != null ? <span>Время: {secondsLeft}с</span> : <span />}
        </div>
        {game}
      </div>
    </div>
  );
}
